import { createContext, useContext, type ComponentPropsWithRef, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { focusRing } from '../lib/styles';

/**
 * CSS-grid table. The design lays every table out as a grid with explicit column
 * widths, so this mirrors that rather than using `<table>` — and carries the ARIA
 * table roles so it still reads correctly.
 *
 * Wide tables scroll horizontally inside their card. They are never squeezed and
 * never clipped (principle 11).
 */

type TableContextValue = { columns: string; minWidth?: number };

const TableContext = createContext<TableContextValue | null>(null);

function useTable(): TableContextValue {
    const ctx = useContext(TableContext);
    if (!ctx) throw new Error('Table parts must be rendered inside <Table>.');
    return ctx;
}

export type TableProps = Omit<ComponentPropsWithRef<'div'>, 'children'> & {
    /**
     * The grid template, straight from the screen spec, e.g.
     * `"84px 132px 118px minmax(0,1fr) 132px 106px 96px"` for Keuringen.
     */
    columns: string;
    /** Below this the table scrolls horizontally. 940 for Keuringen, 640 for invoices. */
    minWidth?: number;
    /** Accessible name, e.g. "Keuringen". */
    label: string;
    children: ReactNode;
};

export function Table({ columns, minWidth, label, className, children, ...rest }: TableProps) {
    return (
        <TableContext.Provider value={{ columns, minWidth }}>
            <div className={cn('overflow-x-auto overscroll-x-contain', className)} {...rest}>
                <div
                    role="table"
                    aria-label={label}
                    className="font-[family-name:var(--font-body)] text-[var(--text-primary)]"
                    style={minWidth ? { minWidth: `${minWidth}px` } : undefined}
                >
                    {children}
                </div>
            </div>
        </TableContext.Provider>
    );
}

export type TableHeadProps = ComponentPropsWithRef<'div'> & {
    /** Sticky by default: the header stays put while the body scrolls. */
    sticky?: boolean;
};

/** Header row: `--surface-subtle`, mono 10px, letter-spacing 0.06em. */
export function TableHead({ sticky = true, className, children, ...rest }: TableHeadProps) {
    const { columns } = useTable();

    return (
        <div role="rowgroup">
            <div
                role="row"
                className={cn(
                    'grid border-b border-[var(--border-default)] bg-[var(--surface-subtle)]',
                    'font-[family-name:var(--font-mono)] text-[10px] tracking-[0.06em] text-[var(--text-tertiary-safe)]',
                    sticky && 'sticky top-0 z-[2]',
                    className,
                )}
                style={{ gridTemplateColumns: columns }}
                {...rest}
            >
                {children}
            </div>
        </div>
    );
}

export type TableHeaderCellProps = ComponentPropsWithRef<'div'> & {
    align?: 'start' | 'end';
};

export function TableHeaderCell({
    align = 'start',
    className,
    children,
    ...rest
}: TableHeaderCellProps) {
    return (
        <div
            role="columnheader"
            className={cn('px-3 py-[10px] uppercase', align === 'end' && 'text-right', className)}
            {...rest}
        >
            {children}
        </div>
    );
}

export function TableBody({ className, children, ...rest }: ComponentPropsWithRef<'div'>) {
    return (
        <div role="rowgroup" className={className} {...rest}>
            {children}
        </div>
    );
}

export type TableRowProps = Omit<ComponentPropsWithRef<'div'>, 'onSelect'> & {
    /** Selected rows get `--accent-tint` plus `inset 2px 0 0` in the accent. */
    selected?: boolean;
    /** Makes the row a keyboard-operable option. Omit for static tables. */
    onSelect?: () => void;
};

export function TableRow({
    selected = false,
    onSelect,
    className,
    children,
    onClick,
    onKeyDown,
    ...rest
}: TableRowProps) {
    const { columns } = useTable();
    const interactive = Boolean(onSelect);

    return (
        <div
            role="row"
            aria-selected={interactive ? selected : undefined}
            tabIndex={interactive ? 0 : undefined}
            onClick={
                interactive
                    ? (event) => {
                          onClick?.(event);
                          if (event.defaultPrevented) return;
                          onSelect?.();
                      }
                    : onClick
            }
            onKeyDown={
                interactive
                    ? (event) => {
                          onKeyDown?.(event);
                          if (event.defaultPrevented) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onSelect?.();
                          }
                      }
                    : onKeyDown
            }
            className={cn(
                'grid items-center border-b border-[var(--border-subtle-2)]',
                selected
                    ? 'bg-[var(--accent-tint)] shadow-[inset_2px_0_0_var(--accent)]'
                    : 'bg-[var(--surface-card)]',
                interactive && 'cursor-pointer',
                interactive && !selected && 'hover:bg-[var(--surface-row-hover)]',
                interactive && focusRing,
                className,
            )}
            style={{ gridTemplateColumns: columns }}
            {...rest}
        >
            {children}
        </div>
    );
}

export type TableCellProps = ComponentPropsWithRef<'div'> & {
    align?: 'start' | 'end';
    /** Amounts, IDs and times are monospaced. */
    mono?: boolean;
    /** Row title column: 13px / 500. */
    emphasis?: boolean;
    /** Secondary column: `--text-tertiary-safe` (4.5:1 on white AND on a selected row). */
    muted?: boolean;
    /** One line with an ellipsis — addresses and inspector names. */
    truncate?: boolean;
};

export function TableCell({
    align = 'start',
    mono = false,
    emphasis = false,
    muted = false,
    truncate = false,
    className,
    children,
    ...rest
}: TableCellProps) {
    return (
        <div
            role="cell"
            className={cn(
                'min-w-0 px-3 py-[11px] text-[13px] leading-[1.35]',
                align === 'end' && 'text-right',
                mono && 'font-[family-name:var(--font-mono)] text-[12px]',
                emphasis && 'font-medium',
                muted ? 'text-[var(--text-tertiary-safe)]' : 'text-[var(--text-primary)]',
                truncate && 'overflow-hidden text-ellipsis whitespace-nowrap',
                className,
            )}
            {...rest}
        >
            {children}
        </div>
    );
}

/** Closing note under a table ("In het PDF-rapport staat elk onderdeel uitgewerkt..."). */
export function TableFootNote({ className, children, ...rest }: ComponentPropsWithRef<'div'>) {
    return (
        <div
            className={cn(
                'px-3 py-[13px] font-[family-name:var(--font-body)] text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]',
                className,
            )}
            {...rest}
        >
            {children}
        </div>
    );
}
